import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Input, Select, Switch, Tooltip, Typography } from 'antd';

import CharacterCounter from 'components/CharacterCounter';
import CustomMaskedInput from 'components/MaskedInput';
import type IProduct from 'interfaces/IProduct';
import { FormContent } from 'scenes/Producer/Product/EditProduct/components/FormContent';
import { EChargeType, EProductType } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import Subscription from './components/Subscription';
import Validations from './validations';

interface IProps {
  product: IProduct;
  enabledValidation: boolean;
  handleChange?: any;
  setRefField?: any;
  setRefError?: any;
  lockedChargeType?: boolean;
}

function errorFor(validationErrors: any, field: any) {
  return validationErrors[field] || '';
}

function isValid(validationErrors: any, field: any) {
  return !!validationErrors[field];
}

function Sale(props: IProps) {
  const { handleChange, product, setRefField, setRefError, enabledValidation, lockedChargeType } = props;
  const { user } = useUserStore(({ user }) => ({ user }));
  const [validationErrors, setValidationErrors] = useState<any>([]);
  const [userHasAccessToSubscriptionModule, setUserHasAccessToSubscriptionModule] = useState(false);
  const prevPropsRef = useRef<{ product: IProduct; enabledValidation: boolean } | null>(null);

  const getPriceHelperText = () => {
    if (isValid(validationErrors, 'price')) return errorFor(validationErrors, 'price');
    const isInfo = product.type === EProductType.DIGITAL;
    const isSinglePayment = product.charge_type === EChargeType.SINGLE;
    if (isInfo && isSinglePayment)
      return 'Você poderá cadastrar o preço de venda inclusive para moedas estrangeiras na Edição';
    return 'EX: 1.000,00';
  };

  const userHasAccessToOpenPriceSubscriptionModule =
    eduzzAccountsService.verifyUserHasBetaModule('valor_aberto_assinaturas');

  const disableOpenPriceBlockedFields = useCallback(() => {
    if (!handleChange) return;
    if (product.trial) handleChange('trial', false);
    if (product.membership_fee) handleChange('membership_fee', false);
    if (product.allows_upgrade) handleChange('allows_upgrade', false);
    if (product.allows_downgrade) handleChange('allows_downgrade', false);
  }, [handleChange, product.trial, product.membership_fee, product.allows_upgrade, product.allows_downgrade]);

  const handleChangeChargeType = useCallback(
    (chargeType: string) => {
      if (!handleChange) return;
      if (chargeType !== EChargeType.SUBSCRIPTION && product.open_price) handleChange('open_price', false);
      handleChange('charge_type', chargeType);
    },
    [handleChange, product.open_price]
  );

  const handleChangeOpenPrice = useCallback(
    (openPrice: boolean) => {
      if (!handleChange) return;
      handleChange('open_price', openPrice);
      if (openPrice) disableOpenPriceBlockedFields();
    },
    [handleChange, disableOpenPriceBlockedFields]
  );

  useEffect(() => {
    setUserHasAccessToSubscriptionModule(eduzzAccountsService.verifyUserHasBetaModule('planos_assinatura'));
  }, []);

  useEffect(() => {
    const prevProps = prevPropsRef.current;

    if (prevProps === null) {
      prevPropsRef.current = { product, enabledValidation };
      return;
    }

    const hasEnabledValidation = !prevProps.enabledValidation && enabledValidation;
    const hasChangedProductWithValidation = enabledValidation && product !== prevProps.product;
    const hasChangedProduct = product !== prevProps.product;
    if (hasEnabledValidation || hasChangedProductWithValidation) setValidationErrors(run(product, Validations));
    if (hasChangedProduct && product.charge_type !== EChargeType.SUBSCRIPTION && product.open_price && handleChange)
      handleChange('open_price', false);
    if (hasChangedProduct && product.charge_type === EChargeType.SUBSCRIPTION && product.open_price)
      disableOpenPriceBlockedFields();
    prevPropsRef.current = { product, enabledValidation };
  }, [product, enabledValidation, handleChange, disableOpenPriceBlockedFields]);

  const productFriendlyNamePrefix = 'EDZ';
  const allowRecurrence =
    product.type === EProductType.DIGITAL ||
    product.type === EProductType.SERVICE ||
    (user.subscription_physical && product.type === EProductType.PHYSICAL);
  const allowOpenValue = product.type === EProductType.DIGITAL || product.type === EProductType.SERVICE;
  const isSubscription = product.charge_type === EChargeType.SUBSCRIPTION;

  const chargeTypeOptions = [
    { label: 'Única', value: EChargeType.SINGLE },
    ...(allowRecurrence && !userHasAccessToSubscriptionModule
      ? [{ label: 'Assinatura/Recorrência', value: EChargeType.SUBSCRIPTION }]
      : []),
    ...(allowOpenValue ? [{ label: 'Valor Aberto', value: EChargeType.OTHER }] : []),
    ...(product.type !== EProductType.PHYSICAL ? [{ label: 'Gratuito', value: EChargeType.FREE }] : [])
  ];

  return (
    <FormContent title='Formas de cobrança' loading={false}>
      <div className='flex flex-col gap-4'>
        <div>
          <Typography.Text className='mb-1 block'>Tipo de cobrança<Typography.Text  className='text-(--uizz-antd-color-error)'>*</Typography.Text ></Typography.Text>
          {lockedChargeType ? (
            <Tooltip title='Esta informação é definida pelo tipo de produto selecionado' placement='top'>
              <Select
                id='new-product-charge-type'
                className='w-full'
                disabled
                value={product.charge_type}
                options={[{ label: 'Assinatura/Recorrência', value: EChargeType.SUBSCRIPTION }]}
              />
            </Tooltip>
          ) : (
            <Select
              id='new-product-charge-type'
              className='w-full'
              value={product.charge_type}
              onChange={(value: any) => handleChangeChargeType(value)}
              options={chargeTypeOptions}
            />
          )}
        </div>

        {isSubscription && userHasAccessToOpenPriceSubscriptionModule && (
          <div>
            <Typography.Text className='flex items-center gap-2'>
              <Switch
                id='new-product-subscription-open-price'
                checked={product.open_price}
                onChange={checked => handleChangeOpenPrice(checked)}
              />
              Valor Aberto
            </Typography.Text>
            <Typography.Text className='text-content-subtitle mt-1 block text-xs'>
              Valor mínimo a partir de R$ 5,00
            </Typography.Text>
          </div>
        )}

        {product.charge_type === EChargeType.SINGLE && (
          <div className='flex flex-col gap-4 sm:flex-row sm:items-start'>
            <div className='flex w-auto flex-col gap-1'>
              <Typography.Text>Moeda</Typography.Text>
              <Input disabled value='BRL (Real Brasileiro)' />
            </div>
            <div className='min-w-0 flex-1'>
              <CustomMaskedInput
                label='Preço'
                fullWidth={true}
                disableUnderline
                id='new-product-price'
                mask='money'
                helperText={getPriceHelperText()}
                error={isValid(validationErrors, 'price')}
                value={product.price}
                onChange={(value: any) => handleChange('price', value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 20 }}
                startAdornment='R$'
                ref={(ref: any) => setRefField('price', ref)}
              />
            </div>
          </div>
        )}

        {isSubscription && (
          <Subscription
            product={product}
            handleChange={handleChange}
            setRefField={setRefField}
            setRefError={setRefError}
            enabledValidation={enabledValidation}
          />
        )}

        {product.charge_type === EChargeType.OTHER && (
          <div>
            <Typography.Paragraph>
              O valor <strong>minímo</strong> no checkout será de R$ 5,00
            </Typography.Paragraph>
            <Typography.Paragraph>Não será habilitado o parcelamento no checkout</Typography.Paragraph>
          </div>
        )}

        {product.charge_type === EChargeType.FREE && (
          <div className='flex flex-col items-start gap-4 md:flex-row md:items-center'>
            <img src='/img/eduzzinho-thumbsup.png' alt='Eduzzinho' width='100' className='shrink-0' />
            <div>
              <b>Eba! Produto gratuito.</b>
              <Typography.Paragraph>
                Produto gratuito é uma ótima maneira de captar leads, você pode utilizar como produto de entrada para
                outro produto pago.
              </Typography.Paragraph>
            </div>
          </div>
        )}

        <CharacterCounter
          helperText={
            isValid(validationErrors, 'friendly_name')
              ? errorFor(validationErrors, 'friendly_name')
              : 'A identificação na fatura do cartão é importante para prevenir chargebacks. Utilize um nome intuitivo que seu cliente irá reconhecer ao ver a fatura'
          }
          label='Nome exibido na fatura de cartão'
          id='friendly_name'
          error={isValid(validationErrors, 'friendly_name')}
          value={
            product.friendly_name && product.friendly_name.startsWith(`${productFriendlyNamePrefix} `)
              ? product.friendly_name.slice(4)
              : product.friendly_name
          }
          onChange={(e: any) => handleChange('friendly_name', e.target.value)}
          prefix={productFriendlyNamePrefix}
          limits={10}
          minimum={0}
          rows={1}
        />
      </div>
    </FormContent>
  );
}

export default Sale as any;
